/**
 * <s-sticky-atc> — sticky add-to-cart bar for the product page.
 *
 * Slides up once the main add-to-cart button has scrolled out of view above the viewport,
 * and hides again over the footer or any [data-sticky-atc-hide] element. It never submits on its own: the bar button clicks the
 * real form button, so the selected bundle, band colors, discounts and cart drawer behave
 * exactly like the main button.
 *
 * Required markup:
 *   data-target — CSS selector for the main add-to-cart button.
 *   [data-sticky-button] — the bar's button.
 * Optional outlets:
 *   [data-sticky-price] — mirrors the main button's [data-offer-cart-price] text.
 *   [data-sticky-quantity] — shows the selected quantity using data-label-one / data-label-other ("{count}").
 */
class SStickyAtc extends HTMLElement {
  #ctaVisible = true;
  #ctaAbove = false;
  #footerVisible = false;
  #observers = [];
  #controller = new AbortController();

  connectedCallback() {
    const opts = { signal: this.#controller.signal };
    this.querySelector('[data-sticky-button]')?.addEventListener('click', () => this.#target?.click(), opts);

    const ctaObserver = new IntersectionObserver(([entry]) => {
      this.#ctaVisible = entry.isIntersecting;
      this.#ctaAbove = entry.boundingClientRect.top < 0;
      this.#render();
    });
    if (this.#target) ctaObserver.observe(this.#target);
    this.#observers.push(ctaObserver);

    // Hide over the footer and over any element marked data-sticky-atc-hide (e.g. the last section).
    const hideTargets = Array.from(document.querySelectorAll('footer, [role="contentinfo"], [data-sticky-atc-hide]'));
    if (hideTargets.length) {
      const visible = new Set();
      const hideObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target);
          else visible.delete(entry.target);
        });
        this.#footerVisible = visible.size > 0;
        this.#render();
      });
      hideTargets.forEach((target) => hideObserver.observe(target));
      this.#observers.push(hideObserver);
    }

    // Mirror price and quantity whenever the product form changes (tier, color, variant partial swaps).
    const form = this.#target?.closest('form');
    const root = form?.closest('[data-product-form]') || form;
    if (root) {
      const mutationObserver = new MutationObserver(() => this.#sync());
      mutationObserver.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['value'] });
      this.#observers.push(mutationObserver);
      root.addEventListener('change', () => this.#sync(), opts);
    }
    this.#sync();
  }

  disconnectedCallback() {
    this.#observers.forEach((observer) => observer.disconnect());
    this.#observers = [];
    this.#controller.abort();
  }

  get #target() {
    const selector = this.dataset.target;
    return selector ? document.querySelector(selector) : null;
  }

  #render() {
    const show = !this.#ctaVisible && this.#ctaAbove && !this.#footerVisible;
    this.toggleAttribute('data-visible', show);
    this.toggleAttribute('inert', !show);
  }

  #sync() {
    const form = this.#target?.closest('form');
    if (!form) return;
    const price = form.querySelector('[data-offer-cart-price]');
    const priceOutlet = this.querySelector('[data-sticky-price]');
    if (price && priceOutlet && priceOutlet.textContent !== price.textContent) {
      priceOutlet.textContent = price.textContent;
    }
    const quantityOutlet = this.querySelector('[data-sticky-quantity]');
    const quantityInput = form.querySelector('input[name="quantity"]');
    if (quantityOutlet && quantityInput) {
      const count = Number(quantityInput.value) || 1;
      const template = count === 1 ? quantityOutlet.dataset.labelOne : quantityOutlet.dataset.labelOther;
      const text = (template || '').replace('{count}', String(count));
      if (quantityOutlet.textContent !== text) quantityOutlet.textContent = text;
    }
  }
}

if (!customElements.get('s-sticky-atc')) customElements.define('s-sticky-atc', SStickyAtc);
